import { arrayMove } from "@dnd-kit/sortable";

import type { QuestionnaireExposureMarkName } from "@digital-twins/shared-types";

/**
 * One row of the builder's sortable list. Question rows carry the id
 * `reorderResearchQuestions` persists. Non-question rows (exposure-mark
 * dividers, and any later droppable that is not a question) leave
 * `questionId` null so a drop on them is still resolved as a question move.
 */
export interface ResearchListDragRow {
  id: string;
  questionId: string | null;
  stableKey: string | null;
  mark: QuestionnaireExposureMarkName | null;
}

export type ResearchListDragUnavailableReason =
  | "locked"
  | "unknown-target"
  | "unchanged";

export type ResearchListDragDecision =
  | { kind: "ignore" }
  | { kind: "reorder"; fromId: string; toId: string }
  | {
      kind: "move-mark";
      mark: QuestionnaireExposureMarkName;
      stableKey: string | null;
    }
  | { kind: "unavailable"; reason: ResearchListDragUnavailableReason };

/**
 * i18n key for an unavailable drop. A lock uses the builder's existing busy
 * line when the panel has one; this key is the fallback.
 */
export function researchDragFeedbackKey(
  reason: ResearchListDragUnavailableReason,
):
  | "questionnaire.reorderUnavailable"
  | "questionnaire.reorderUnknownTarget"
  | "questionnaire.reorderUnchanged" {
  if (reason === "locked") {
    return "questionnaire.reorderUnavailable";
  }
  if (reason === "unknown-target") {
    return "questionnaire.reorderUnknownTarget";
  }
  return "questionnaire.reorderUnchanged";
}

/**
 * What a research-list drag should commit.
 *
 * The sortable list mixes questions with non-question rows. `@dnd-kit`
 * reports whichever row is under the pointer, so a question drop often lands
 * on an exposure-mark divider. That drop is still a question move: the active
 * row is inserted at the over row's index — the same move `arrayMove` and
 * `reorderResearchQuestions` use — and the resulting question order is
 * expressed as `(fromId, toId)` for the dirty `move_question` path.
 *
 * Releasing outside the list, or on the same row, is `ignore`. A drop on a
 * non-question row that does not change question order is `unavailable`, so
 * the editor can say so instead of looking frozen. Dragging a mark still
 * moves that mark; `null` declares an empty zone.
 */
export function resolveResearchListDrag(input: {
  activeId: string;
  overId: string | null;
  isLocked: boolean;
  rows: Array<ResearchListDragRow>;
}): ResearchListDragDecision {
  if (!input.overId || input.activeId === input.overId) {
    return { kind: "ignore" };
  }
  if (input.isLocked) {
    return { kind: "unavailable", reason: "locked" };
  }

  const oldIndex = input.rows.findIndex((row) => row.id === input.activeId);
  const newIndex = input.rows.findIndex((row) => row.id === input.overId);
  if (oldIndex === -1 || newIndex === -1) {
    return { kind: "unavailable", reason: "unknown-target" };
  }

  const active = input.rows[oldIndex];
  const over = input.rows[newIndex];
  const reordered = arrayMove(input.rows, oldIndex, newIndex);

  if (active.mark) {
    return {
      kind: "move-mark",
      mark: active.mark,
      stableKey: stableKeyBesideMark(reordered, active.mark),
    };
  }

  if (!active.questionId) {
    return { kind: "unavailable", reason: "unknown-target" };
  }

  const target = questionReorderTarget(
    input.rows,
    reordered,
    active.questionId,
  );
  if (target) {
    return { kind: "reorder", ...target };
  }
  if (over.questionId === null) {
    return { kind: "unavailable", reason: "unchanged" };
  }
  return { kind: "ignore" };
}

/**
 * `toId` is the question that currently occupies the landing index.
 * `reorderResearchQuestions` inserts the moved question at that question's
 * index, which is where it sits in `reordered`.
 */
function questionReorderTarget(
  original: Array<ResearchListDragRow>,
  reordered: Array<ResearchListDragRow>,
  activeQuestionId: string,
): { fromId: string; toId: string } | null {
  const before = original.filter((row) => row.questionId !== null);
  const after = reordered.filter((row) => row.questionId !== null);
  const fromIndex = before.findIndex(
    (row) => row.questionId === activeQuestionId,
  );
  const toIndex = after.findIndex((row) => row.questionId === activeQuestionId);
  if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
    return null;
  }
  const toId = before[toIndex]?.questionId;
  if (!toId || toId === activeQuestionId) {
    return null;
  }
  return { fromId: activeQuestionId, toId };
}

/**
 * The question the moved mark now sits against. Reveal looks upward (the last
 * pre-exposure question). Shared view looks downward (the first question in
 * that zone). No question on that side declares an empty zone (`null`), which
 * is a real position, not an unset mark.
 */
function stableKeyBesideMark(
  reordered: Array<ResearchListDragRow>,
  mark: QuestionnaireExposureMarkName,
): string | null {
  const dividerPosition = reordered.findIndex((row) => row.mark === mark);
  if (dividerPosition === -1) {
    return null;
  }
  const step = mark === "reveal" ? -1 : 1;
  for (
    let index = dividerPosition + step;
    index >= 0 && index < reordered.length;
    index += step
  ) {
    const stableKey = reordered[index]?.stableKey;
    if (stableKey) {
      return stableKey;
    }
  }
  return null;
}
